<template>
  <!--
    Panel de info destacada del rail. 4 estados vía data-template:
      empty      -> no renderiza (guarda v-if; tampoco renderiza si textHtml viene vacío)
      text       -> solo panel de texto
      text-link  -> texto + CTA pill (external-link) que abre un link
      text-video -> texto + CTA pill (play) que abre VideoModal
    Datos por props: los mapea `helpers/sidebarHelpers.js` desde clc_new_home_page.
  -->
  <div
    v-if="isVisible"
    class="SidebarHighlight"
    :data-template="template"
  >
    <div class="SidebarHighlight__panel">
      <!-- textHtml admite <strong> para la mezcla Bold/Regular del Figma
           (llega ya renderizado desde el rich text de Prismic). -->
      <p class="SidebarHighlight__text" v-html="textHtml"></p>
    </div>

    <!-- CTA tipo Link -->
    <a
      v-if="template === 'text-link'"
      class="SidebarHighlight__cta"
      :href="ctaUrl"
      :target="ctaTarget"
      rel="noopener"
      :data-label="ctaLabel"
    >
      <span class="SidebarHighlight__icon">
        <svg class="linkType" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#252836" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 3h6v6"/>
          <path d="M10 14 21 3"/>
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        </svg>
      </span>
      <span class="SidebarHighlight__label">{{ ctaLabel }}</span>
    </a>

    <!-- CTA tipo Video -->
    <button
      v-else-if="template === 'text-video'"
      type="button"
      class="SidebarHighlight__cta"
      :data-label="ctaLabel"
      @click="openVideo"
    >
      <span class="SidebarHighlight__icon">
        <svg class="videoType" xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none">
          <path d="M18.75 9.27a2 2 0 0 1 0 3.46L3 21.83a2 2 0 0 1-3-1.73V1.9A2 2 0 0 1 3 .17l15.75 9.1Z" fill="#252836"/>
        </svg>
      </span>
      <span class="SidebarHighlight__label">{{ ctaLabel }}</span>
    </button>

    <!-- Montaje permanente: se abre/cierra con :show, NO con v-if (ver nota del portal en VideoModal). -->
    <VideoModal
      :show="showVideo"
      :type="videoType"
      :video-id="videoId"
      :title="ctaLabel"
      @close="showVideo = false"
    />
  </div>
</template>

<script>
import VideoModal from './VideoModal.vue';
import { hasContent } from '../helpers/sidebarHelpers';

export default {
  name: 'SidebarHighlight',
  components: { VideoModal },
  props: {
    // Estado del panel. Mapea 1:1 al data-template del Figma.
    template: {
      type: String,
      default: 'empty',
      validator: (value) => ['empty', 'text', 'text-link', 'text-video'].includes(value)
    },
    // Texto destacado. HTML permitido (<strong> para el mix Bold/Regular).
    textHtml: {
      type: String,
      default: ''
    },
    // Etiqueta del CTA (ambos tipos).
    ctaLabel: {
      type: String,
      default: 'Know more about CLC'
    },
    // text-link: destino del link.
    ctaUrl: {
      type: String,
      default: '#'
    },
    ctaTarget: {
      type: String,
      default: '_blank'
    },
    // text-video: config del modal agnóstico.
    videoType: {
      type: String,
      default: 'jwplayer',
      validator: (value) => ['jwplayer', 'youtube'].includes(value)
    },
    videoId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showVideo: false
    };
  },
  computed: {
    // Guarda de render: además del template, exige texto real. Cubre el caso de Prismic con
    // el toggle en ON y los campos sin cargar (pasaba en es-es) y el cache viejo en sessionStorage.
    isVisible() {
      return this.template !== 'empty' && hasContent(this.textHtml);
    }
  },
  methods: {
    openVideo() {
      // Analytics / GTM: el trigger de click por data-label no captura los <button>, así que
      // el CTA de video empuja el evento explícitamente (mismo valor que el data-label).
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'click_element',
        label: this.ctaLabel
      });
      if (this.videoId) {
        this.showVideo = true;
      }
    }
  }
};
</script>
